import { Link, useNavigate, useParams } from "react-router-dom";
import { PageWidth, ScorePill, StarRow } from "./components/travel-ui";
import { useMarketplaceSearch } from "./context/SearchContext";
import { getHotelById, nightsBetween } from "./data/mock-hotels";
import { formatMoney, formatShortDate } from "./lib/format";
import { quoteStay } from "./lib/pricing";
import { useWallet } from "./services/wallet.service";

export default function MemberHotelDetails() {
  const { hotelId = "" } = useParams();
  const navigate = useNavigate();
  const { search } = useMarketplaceSearch();
  const wallet = useWallet();
  const hotel = getHotelById(hotelId);
  const nights = nightsBetween(search.checkIn, search.checkOut);

  if (!hotel) {
    return (
      <PageWidth>
        <p className="font-semibold">That stay is not in the demo list.</p>
        <Link to="/member/hotels" className="mt-2 inline-block text-[#006ce4]">
          Back to stays
        </Link>
      </PageWidth>
    );
  }

  return (
    <PageWidth>
      <p className="text-sm text-[#595959]">
        <Link to="/member/hotels" className="text-[#006ce4]">
          Stays
        </Link>{" "}
        / {hotel.city} / {hotel.name}
      </p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <StarRow count={hotel.stars} />
          <h1 className="text-3xl font-bold">{hotel.name}</h1>
          <p className="text-sm text-[#595959]">
            {hotel.city}, {hotel.state}, {hotel.country}
          </p>
        </div>
        <ScorePill rating={hotel.rating} reviews={hotel.reviews} />
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-[2fr_1fr]">
        <img
          src={hotel.image}
          alt={`${hotel.name} exterior`}
          className="h-72 w-full rounded-lg object-cover"
        />
        <div className="grid gap-2">
          {hotel.gallery.slice(0, 2).map((src) => (
            <img
              key={src}
              src={src}
              alt=""
              className="h-[8.75rem] w-full rounded-lg object-cover"
            />
          ))}
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_300px]">
        <div>
          <p className="max-w-2xl text-sm leading-6">{hotel.description}</p>
          <ul className="mt-4 flex flex-wrap gap-2">
            {hotel.amenities.map((item) => (
              <li
                key={item}
                className="rounded-full bg-white px-3 py-1 text-xs font-medium ring-1 ring-[#e7e7e7]"
              >
                {item}
              </li>
            ))}
          </ul>

          <h2 className="mt-8 text-xl font-bold">Choose a room</h2>
          <p className="text-sm text-[#595959]">
            {nights > 0
              ? `${formatShortDate(search.checkIn)} – ${formatShortDate(search.checkOut)} · ${nights} night${nights === 1 ? "" : "s"} · ${search.guests} adults · ${search.rooms} room${search.rooms === 1 ? "" : "s"}`
              : "Set your dates on the search page to see a total."}
          </p>
          <div className="mt-4 space-y-3">
            {hotel.rooms.map((room) => {
              const quote = quoteStay(
                room,
                search.checkIn,
                search.checkOut,
                search.rooms,
              );
              return (
                <article
                  key={room.id}
                  className="grid gap-4 rounded-lg border border-[#e7e7e7] bg-white p-4 md:grid-cols-[160px_1fr_auto]"
                >
                  <img
                    src={room.image}
                    alt=""
                    className="h-28 w-full rounded-md object-cover"
                  />
                  <div>
                    <h3 className="font-bold">{room.name}</h3>
                    <p className="text-sm text-[#595959]">
                      {room.beds} · sleeps {room.occupancy}
                    </p>
                    <p className="mt-1 text-sm text-[#008009]">{room.mealPlan}</p>
                    <p className="text-sm text-[#008009]">{room.cancellation}</p>
                  </div>
                  <div className="text-left md:text-right">
                    <p className="text-xs font-semibold text-[#008009]">Member price</p>
                    <p className="text-2xl font-bold">
                      {formatMoney(quote?.total ?? room.pricePerNight, wallet.currency)}
                    </p>
                    <p className="text-xs text-[#595959]">
                      {quote ? `${quote.nights} nights · taxes included` : "per night"}
                    </p>
                    <p className="mt-1 text-xs text-[#595959]">
                      Earn {quote?.pointsEarn ?? room.pointsEarn} pts
                    </p>
                    <button
                      type="button"
                      className="mt-3 h-10 rounded-md bg-[#006ce4] px-4 text-sm font-semibold text-white hover:bg-[#0057b8]"
                      onClick={() =>
                        navigate(
                          `/member/booking/${hotel.id}?room=${encodeURIComponent(room.id)}&checkIn=${search.checkIn}&checkOut=${search.checkOut}&guests=${search.guests}&rooms=${search.rooms}`,
                        )
                      }
                    >
                      Reserve
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        <aside className="h-fit rounded-lg border border-[#e7e7e7] bg-white p-4 text-sm">
          <p className="font-bold">House rules</p>
          <p className="mt-2 text-[#595959]">Check-in {hotel.checkIn}</p>
          <p className="text-[#595959]">Check-out {hotel.checkOut}</p>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-[#595959]">
            {hotel.highlights.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </aside>
      </div>
    </PageWidth>
  );
}
